import { formatNumber } from "pages/MatchDetail/MatchedOdds";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import "./styles.scss"

interface FancyPLData {
  sport_name: string;
  match_name: string;
  match_id: string;
  sport_id: string;
  type: number;
  market_name?: string;
  winner_name?: string;
  user_pl?: number;
  commission: number;
  total: number;
  result_date?: string;
  event_id: string;
  user_id: string;
  settle_date_time: string;
  user_name: string;
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  onClickRow: (match_id: string, match_name: string, sport_name: string, user_id: string) => void;
  searchTerm?: string; // Optionally pass a search term as a prop
}

const formatDateTime = (dateString?: string) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const PlUserwiseTableEventtable: React.FC<FancyPLTableProps> = ({ fancyPLData, onClickRow, searchTerm = "" }) => {
  // State for sorting
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [searchQuery] = useState(searchTerm);

  // Handle column sorting
  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  // Get sorting class for the column
  const getSortClass = (key: keyof FancyPLData) => {
    const isActive = sortKey === key;
    const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
    const downClass = isActive && sortOrder === "desc" ? "text-dark" : "text-muted";
    return (
      <span className="d-flex flex-column ms-2">
        <i className={`bi bi-caret-up-fill ${upClass}`} style={{ lineHeight: 0.6 }} />
        <i className={`bi bi-caret-down-fill ${downClass}`} style={{ lineHeight: 0.6 }} />
      </span>
    );
  };

  // Sort the data based on the sort key and order
  const sortedData = sortKey
    ? [...fancyPLData].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];

        // Sorting for string values
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // Sorting for numeric values
        const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal as string);
        const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal as string);

        if (isNaN(aNum) || isNaN(bNum)) return 0;

        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      })
    : fancyPLData;

  // Filter data based on search term
  const filteredData = sortedData.filter((item) =>
    Object.values(item).some((value) =>
      value
        ? value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        : false
    )
  );

  return (
    <div className="table-responsive">
    

      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th onClick={() => handleSort("user_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>User Name</span>
                {getSortClass("user_name")}
              </div>
            </th>
            <th onClick={() => handleSort("sport_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name")}
              </div>
            </th>
            <th onClick={() => handleSort("match_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Event Name</span>
                {getSortClass("match_name")}
              </div>
            </th>
            <th onClick={() => handleSort("market_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Market Name</span>
                {getSortClass("market_name")}
              </div>
            </th>
            <th onClick={() => handleSort("winner_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Result</span>
                {getSortClass("winner_name")}
              </div>
            </th>
            <th onClick={() => handleSort("user_pl")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Profit/Loss</span>
                {getSortClass("user_pl")}
              </div>
            </th>
            <th onClick={() => handleSort("commission")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Commission</span>
                {getSortClass("commission")}
              </div>
            </th>
            <th onClick={() => handleSort("settle_date_time")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Settle Time</span>
                {getSortClass("settle_date_time")}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item, index) => (
            <tr
              key={index}
              onClick={() =>
                onClickRow(item.match_id, item.match_name, item.sport_name, item.user_id)
              }
              style={{ cursor: "pointer" }}
            >
              <td>{item.user_name}</td>
              <td>{item.sport_name}</td>
              <td>{item.match_name}</td>
              <td>{item.market_name || "-"}</td>
              <td>{item.winner_name || "-"}</td>
              <td
                className={(item.user_pl ?? 0) < 0 ? "text-danger" : "text-success"}
              >
                {formatNumber((item.user_pl ?? 0).toFixed(2))}
              </td>
              <td className={item.commission < 0 ? "text-danger" : "text-success"}>
                {formatNumber(item.commission.toFixed(2))}
              </td>
              <td>{formatDateTime(String(item.settle_date_time))}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default PlUserwiseTableEventtable;
